<script setup lang="ts">
import { XIcon } from '@clicksign/excalibur';
import { useI18n } from '#i18n';

import { BrandButton } from '~~base-ui';
import { useCurrentSignatureRequestKey } from '~~core';

export type CaptureFeedbackMessageProps = {
  status?: string;
};

const { t } = useI18n();

const localePath = useLocalePath();
const gtm = useGtm();
const props = defineProps<CaptureFeedbackMessageProps>();

const { signatureRequestKey } = useCurrentSignatureRequestKey();

const isPending = computed(() => props.status === 'pending');
const isFacialBiometricsFailed = computed(() => props.status === 'facial_biometrics_rejected' || props.status === 'facial_biometrics_failed');
const isFacialBiometricsLimitExceeded = computed(() => props.status === 'facial_biometrics_limit_exceeded');
const isDocumentscopyFailed = computed(() => props.status === 'documentscopy_rejected');
const isDigitalCertifiedError = computed(() => props.status === 'digital_certificate_pdfa_error');
const isBiometricError = computed(() => props.status === 'biometric_error');

const titleMessage = computed(() => {
  if (isDigitalCertifiedError.value) return t('requirement.digitalCertificate.failed.title');
  if (isBiometricError.value) return t('requirement.biometric.failed.title');

  return isPending.value ? t('requirement.facialBiometrics.facematchPending.title') : t('requirement.facialBiometrics.facematchRefused.title');
});

const descriptionPath = computed(() => {
  if (isDigitalCertifiedError.value) return 'requirement.digitalCertificate.failed.subtitle';
  if (isBiometricError.value) return 'requirement.biometric.failed.description';
  if (isFacialBiometricsLimitExceeded.value) return 'requirement.facialBiometrics.error.facematch.exceededLimit';

  return isPending.value ? 'requirement.facialBiometrics.facematchPending.description' : 'requirement.facialBiometrics.facematchRefused.description';
});

const backTo = () => {
  if (isFacialBiometricsFailed.value) {
    gtm?.trackEvent({
      event: 'ga-send',
      ga_event_category: 'button-try-again-facial-biometrics',
      ga_event_name: 'sign-step-widget-app',
    });

    navigateTo(localePath({
      name: 'signatureRequestKind-signatureRequestKey-requirement-facial-biometrics',
      params: { signatureRequestKey: signatureRequestKey.value },
    }));
  } else if (isDocumentscopyFailed.value) {
    navigateTo(localePath({
      name: 'signatureRequestKind-signatureRequestKey-requirement-documentscopy',
      params: { signatureRequestKey: signatureRequestKey.value },
    }));
  }
};
</script>

<template>
  <div :class="$style.wrapper">
    <XIcon
      :class="[isPending ? $style.pendingIcon : $style.refusedIcon]"
      :icon="isPending ? 'clock' : 'times-circle'"
      size="3x"
    />

    <h3
      :class="$style.title"
      data-testid="signatureFeedbackMessage"
    >
      {{ titleMessage }}
    </h3>

    <div>
      <p :class="$style.description">
        {{ t(descriptionPath) }}
      </p>
      <BrandButton
        v-if="isFacialBiometricsFailed || isDocumentscopyFailed"
        :class="$style.tryAgainFacematch"
        data-testid="captureFeedbackMessageTryAgain"
        @click="backTo"
      >
        <XIcon
          icon="arrow-left"
          left
        />
        {{ $t('requirement.facialBiometrics.facematchRefused.button') }}
      </BrandButton>
    </div>
  </div>
</template>

<style lang="scss" module>
.wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: var(--space-medium) var(--space-medium) var(--space-small-xx);
  text-align: center;
}

.title {
  margin-bottom: var(--space-medium);
  font-size: var(--font-size-medium);

  @include breakpoint('small') {
    margin-bottom: var(--space-small-x);
    font-size: var(--font-size-large);
  }
}

.description {
  font-size: var(--font-size-small);
  white-space: pre-line;

  @include breakpoint('small') {
    font-size: var(--font-size-regular);
  }
}

.pendingIcon,
.refusedIcon {
  margin-bottom: var(--space-small-xx);
}

.pendingIcon {
  color: var(--color-feedback-warning-400);
}

.refusedIcon {
  color: var(--color-feedback-error-400);
}

.tryAgainFacematch {
  margin: var(--space-medium) 0;
}

.sub {
  margin-top: var(--space-small-xx);
}
</style>
